import { ProfileCategory, getCategoryForProfile } from './profileCategories';

// Profile type mapping from behavioral pattern codes to descriptive names
export const profileTypeMapping: Record<string, {
  type: string;
  category: ProfileCategory;
  description: string;
  traits: string[];
  workStyle: string;
  communication: string;
}> = {
  'HHHH': {
    type: 'Luminary',
    category: 'GAS',
    description: 'Charismatic leader who builds structured, stable teams and drives organizational excellence',
    traits: ['Visionary', 'Team-oriented', 'Systematic', 'Relationship builder'],
    workStyle: 'Leads through inspiration and structured processes',
    communication: 'Engaging and well-organized communication style'
  },
  'HHHL': {
    type: 'Commander',
    category: 'GLUE',
    description: 'Strong leader who values both relationships and stability, creating lasting organizational impact',
    traits: ['Team builder', 'Consistent performer', 'Diplomatic leader', 'Change-resistant'],
    workStyle: 'Builds consensus while driving results',
    communication: 'Diplomatic yet decisive communication approach'
  },
  'HHLH': {
    type: 'Executive',
    category: 'GAS',
    description: 'Commanding and influential leader who drives results through people and strategic thinking',
    traits: ['Natural leader', 'Persuasive communicator', 'Results-focused', 'Adaptable to change'],
    workStyle: 'Strategic leadership with focus on results',
    communication: 'Persuasive and results-oriented dialogue'
  },
  // HHLL broken down into 4 sub-categories with explicit naming
  'HHLL-BHDH': {
    type: 'Closer',
    category: 'GLUE',
    description: 'High-energy relationship builder who drives change through structured networking and systematic influence',
    traits: ['Relationship-focused', 'Structured approach', 'Influential', 'Network builder'],
    workStyle: 'Builds networks through systematic relationship management',
    communication: 'Enthusiastic and organized networking style'
  },
  'HHLL-AHDH': {
    type: 'Charger',
    category: 'GAS',
    description: 'Commanding leader who drives innovation through structured vision and systematic execution',
    traits: ['Visionary', 'Results-driven', 'Systematic leader', 'Strategic thinker'],
    workStyle: 'Leads through structured vision and systematic planning',
    communication: 'Commanding yet organized leadership style'
  },
  'HHLL-BHDL': {
    type: 'Uniter',
    category: 'GLUE',
    description: 'Patient relationship builder who creates change through collaborative innovation and people-focused solutions',
    traits: ['Collaborative', 'Patient innovator', 'People-focused', 'Relationship builder'],
    workStyle: 'Patient innovation through collaborative relationships',
    communication: 'Collaborative and patient people-centered approach'
  },
  'HHLL-AHDL': {
    type: 'Builder',
    category: 'GAS',
    description: 'Dynamic yet decisive leader who builds rapid innovation through strategic thinking and quick decision-making',
    traits: ['Strategic thinker', 'Dynamic leader', 'Quick decision maker', 'Rapid innovator'],
    workStyle: 'Strategic entrepreneurship with rapid execution',
    communication: 'Strategic and decisive leadership dialogue'
  },
  'HLHH': {
    type: 'Catalyst',
    category: 'GAS',
    description: 'Expert who prefers structured, independent work while maintaining high standards of quality and precision',
    traits: ['Technical expert', 'Detail-oriented', 'Quality-focused', 'Self-sufficient'],
    workStyle: 'Independent expertise with systematic approach',
    communication: 'Precise and technical communication style'
  },
  'HLHL': {
    type: 'Achiever',
    category: 'GAS',
    description: 'Results-driven individual who values consistency and systematic approaches to goal accomplishment',
    traits: ['Goal-oriented', 'Persistent', 'Self-motivated', 'Process-minded'],
    workStyle: 'Systematic goal achievement and consistency',
    communication: 'Clear and goal-focused dialogue'
  },
  'HLLH': {
    type: 'Pioneer',
    category: 'BRAKE',
    description: 'Independent achiever who prefers direct action and thrives in unstructured, challenging environments',
    traits: ['Self-reliant', 'Quick decision maker', 'Task-focused', 'Comfortable with ambiguity'],
    workStyle: 'Independent action in dynamic environments',
    communication: 'Direct and action-oriented exchanges'
  },
  'HLLL': {
    type: 'Independent',
    category: 'GAS',
    description: 'Self-motivated individual who prefers autonomy and direct action while maintaining high personal standards',
    traits: ['Self-reliant', 'Decisive', 'Efficient', 'Direct'],
    workStyle: 'Autonomous and efficient task execution',
    communication: 'Direct and efficient communication style'
  },
  'LHHH': {
    type: 'Connector',
    category: 'BRAKE',
    description: 'Relationship-focused leader who creates structured, inclusive environments where everyone can thrive',
    traits: ['Nurturing', 'Organized', 'Inclusive', 'Process-oriented'],
    workStyle: 'Collaborative team building with structure',
    communication: 'Inclusive and supportive dialogue'
  },
  'LHHL': {
    type: 'Supporter',
    category: 'GLUE',
    description: 'People-focused team player who values stability and creates lasting, meaningful professional relationships',
    traits: ['Loyal', 'Patient', 'Encouraging', 'Reliable'],
    workStyle: 'Supportive and relationship-focused approach',
    communication: 'Encouraging and patient exchanges'
  },
  'LHLH': {
    type: 'Spark',
    category: 'BRAKE',
    description: 'People-focused motivator who embraces change and inspires others through enthusiasm and positive energy',
    traits: ['Enthusiastic', 'Persuasive', 'Optimistic', 'Flexible'],
    workStyle: 'Motivational and adaptable approach',
    communication: 'Enthusiastic and persuasive style'
  },
  'LHLL': {
    type: 'Networker',
    category: 'GLUE',
    description: 'Social connector who thrives in dynamic environments and excels at building extensive professional networks',
    traits: ['Social', 'Spontaneous', 'Creative', 'Energetic'],
    workStyle: 'Dynamic networking and relationship building',
    communication: 'Social and spontaneous exchanges'
  },
  'LLHH': {
    type: 'Systematizer',
    category: 'BRAKE',
    description: 'Process-oriented individual who values quality, consistency, and systematic approaches to work excellence',
    traits: ['Methodical', 'Quality-focused', 'Reliable', 'Thorough'],
    workStyle: 'Systematic and quality-focused execution',
    communication: 'Methodical and thorough dialogue'
  },
  'LLHL': {
    type: 'Steady',
    category: 'BRAKE',
    description: 'Steady team member who maintains harmony, builds consensus, and creates collaborative work environments where everyone feels valued',
    traits: ['Diplomatic', 'Consistent', 'Peaceful', 'Reliable'],
    workStyle: 'Harmonious and consensus-building approach',
    communication: 'Diplomatic and peaceful exchanges'
  },
  'LLLH': {
    type: 'Optimizer',
    category: 'BRAKE',
    description: 'Methodical specialist who improves systems through careful analysis and quality-driven processes in fast-paced settings',
    traits: ['Systematic', 'Quality-focused', 'Process-oriented', 'Methodical'],
    workStyle: 'Systematic optimization with quality focus',
    communication: 'Structured and methodical exchanges'
  },
  'LLLL': {
    type: 'Harmonizer',
    category: 'BRAKE',
    description: 'Adaptable individual who maintains balance in all situations while fostering positive relationships and organizational stability',
    traits: ['Versatile', 'Flexible', 'Peaceful', 'Adaptable'],
    workStyle: 'Harmonious and adaptable approach',
    communication: 'Peaceful and flexible exchanges'
  }
};

export const getProfileType = (patternCode: string) => {
  // Handle the mapping from personality key to pattern code
  const codeMapping: Record<string, string> = {
    'steady': 'LLHL',
    'facilitator': 'LLHL'  // Keep backward compatibility
  };
  
  const mappedCode = codeMapping[patternCode] || patternCode;
  return profileTypeMapping[mappedCode as keyof typeof profileTypeMapping] || {
    type: 'Professional',
    category: 'BRAKE' as ProfileCategory,
    description: 'Demonstrates well-rounded traits across all behavioral dimensions with adaptable professional approach',
    traits: ['Well-rounded', 'Adaptable', 'Versatile', 'Situational'],
    workStyle: 'Adaptable professional approach',
    communication: 'Situational communication style'
  };
};
